import { INTERNAL_CATEGORIES } from '../components/attivita/attivitaConfig';
import { useQuery } from '@tanstack/react-query';
import type { ComponentType } from 'react';
import {
  MapPin,
  Phone,
  Video,
  Mail,
  Users,
  StickyNote,
  FileText,
  ArrowRightCircle,
  UserCheck,
  CheckCircle,
  TrendingUp,
  BellOff,
  Circle,
} from 'lucide-react';
import { activityTypesAPI } from '../utils/api';
import type { ActivityTypeContext, ActivityTypeResponse } from '../types/activityType';

/** Nome lucide (colonna `icona` dell'anagrafica) → componente icona. */
const ICON_MAP: Record<string, ComponentType<{ className?: string }>> = {
  MapPin,
  Phone,
  Video,
  Mail,
  Users,
  StickyNote,
  FileText,
  ArrowRightCircle,
  UserCheck,
  CheckCircle,
  TrendingUp,
  BellOff,
};

/** Nomi icona selezionabili (pagina admin "Tipologie Attività"). */
export const ICON_NAMES = Object.keys(ICON_MAP);

const FALLBACK_ICON = Circle;
const FALLBACK_COLOR = '#6b7280';

/** Componente icona per nome, con fallback (picker admin). */
export function getIconByName(name: string | undefined | null): ComponentType<{ className?: string }> {
  return (name && ICON_MAP[name]) || FALLBACK_ICON;
}

/**
 * Colore per i 12 codici canonici quando l'anagrafica non ha ancora un
 * `colore` esplicito (il seed di default non lo valorizza — vedi
 * DEFAULT_ACTIVITY_TYPES in core/config.py). Un admin può sovrascriverlo
 * dalla pagina "Tipologie Attività"; finché non lo fa, questi valori
 * riprendono la palette già in uso in ActivityAnalytics/LeadActivityTimeline
 * così l'UI non diventa tutta grigia.
 */
const CODICE_FALLBACK_COLOR: Record<string, string> = {
  visita: '#3B82F6',
  telefonata: '#10B981',
  videochiamata: '#8B5CF6',
  email: '#0EA5E9',
  riunione: '#A855F7',
  nota: '#F59E0B',
  form_submit: '#6366F1',
  status_change: '#06B6D4',
  assignment: '#EC4899',
  conversion: '#22C55E',
  score_change: '#F97316',
  unsubscribe: '#EF4444',
};

export function activityTypesQueryKey(context: ActivityTypeContext) {
  return ['activity-types', context] as const;
}

/**
 * Tipologie attività attive per contesto (lead/cliente/offerta), dall'anagrafica
 * `activity_types`. `staleTime` lungo: l'anagrafica cambia raramente rispetto
 * alle attività stesse.
 */
export function useActivityTypes(context: ActivityTypeContext) {
  return useQuery({
    queryKey: activityTypesQueryKey(context),
    queryFn: async () => {
      const res = await activityTypesAPI.getTypes({ context, active_only: true, limit: 100 });
      return res.data.data;
    },
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
  });
}

/** Dashboard needs all contexts, including hidden types to enforce their flags. */
export function useDashboardActivityTypes() {
  return useQuery({
    queryKey: ['activity-types', 'dashboard'],
    queryFn: async () => {
      const types: ActivityTypeResponse[] = [];
      let page = 1;
      let pages = 1;
      do {
        const res = await activityTypesAPI.getTypes({ page, limit: 500 });
        types.push(...res.data.data);
        pages = res.data.pagination.pages;
        page += 1;
      } while (page <= pages);
      return types;
    },
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
  });
}

/** Historical codes remain visible unless an explicit configuration hides them. */
export function filterDashboardActivities<T extends { tipo: string }>(
  activities: T[], types: ActivityTypeResponse[] | undefined,
): T[] {
  const hidden = new Set(types?.filter((t) => t.attivo_dashboard === false).map((t) => t.codice));
  return activities.filter((activity) => !hidden.has(activity.tipo));
}

export interface ResolvedActivityType {
  label: string;
  icon: ComponentType<{ className?: string }>;
  colore: string;
}

/**
 * Label/icona/colore per un codice attività, con fallback quando il codice non
 * è (più) tra i tipi caricati (es. tipo disattivato, anagrafica non ancora
 * risolta, o dato storico con un codice rimosso).
 */
export function resolveActivityType(
  codice: string,
  types: ActivityTypeResponse[] | undefined,
): ResolvedActivityType {
  const match = types?.find((t) => t.codice === codice);
  if (!match) {
    return {
      label: INTERNAL_CATEGORIES[codice as keyof typeof INTERNAL_CATEGORIES] || codice,
      icon: FALLBACK_ICON,
      colore: CODICE_FALLBACK_COLOR[codice] || FALLBACK_COLOR,
    };
  }
  return {
    label: match.label,
    icon: (match.icona && ICON_MAP[match.icona]) || FALLBACK_ICON,
    colore: match.colore || CODICE_FALLBACK_COLOR[match.codice] || FALLBACK_COLOR,
  };
}
